import { Check, Timer, CheckCircle2, ListChecks, AlertTriangle } from "lucide-react";
import { exText } from "@/data/program";

export function ExerciseCard({ exercise, index, checked, onToggle, onTimer, mastered = false }) {
  const hasCues = exercise.cues && exercise.cues.length > 0;
  const hasErrors = exercise.errors && exercise.errors.length > 0;
  const hasImages = exercise.images && exercise.images.length > 0;
  const hasDetails = exercise.desc || hasCues || hasErrors || hasImages;

  return (
    <div
      data-testid={`exercise-card-${index}`}
      className={`border transition-colors ${
        checked ? "border-[#fa003f]/60 bg-[#fa003f]/[0.06]" : "border-zinc-800 bg-[#141414]"
      }`}
    >
      <div className="flex items-stretch">
        <div className="flex-1 p-4 min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">
            {String(index + 1).padStart(2, "0")}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <h3
              className={`font-head text-2xl leading-tight tracking-tight ${
                checked ? "text-zinc-400 line-through decoration-[#fa003f]/70" : "text-white"
              }`}
            >
              {exercise.name}
            </h3>
            {mastered && (
              <span
                data-testid={`exercise-mastered-${index}`}
                className="inline-flex items-center gap-1 border border-[#00f0ff]/50 bg-[#00f0ff]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#00f0ff]"
              >
                <CheckCircle2 size={12} /> Padroneggiato
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-3">
            <p className="text-sm text-zinc-400">{exText(exercise)}</p>
            {exercise.hold != null && (
              <button
                data-testid={`exercise-timer-btn-${index}`}
                onClick={() => onTimer(exercise)}
                className="inline-flex items-center gap-1 border border-[#00f0ff]/40 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-[#00f0ff] active:scale-95 transition-transform"
              >
                <Timer size={12} /> Timer
              </button>
            )}
          </div>
        </div>

        <button
          data-testid={`exercise-checkbox-${index}`}
          aria-label="Segna come fatto"
          onClick={() => onToggle(!checked)}
          className="flex items-center justify-center px-4 active:scale-90 transition-transform"
        >
          <span
            className={`flex h-11 w-11 items-center justify-center border-2 transition-colors ${
              checked ? "border-[#fa003f] bg-[#fa003f] animate-pop" : "border-zinc-700 bg-transparent"
            }`}
          >
            {checked && <Check size={26} strokeWidth={3.5} className="text-white" />}
          </span>
        </button>
      </div>

      {hasDetails && (
        <div className="border-t border-zinc-800 px-4 py-3 space-y-3">
          {hasImages && (
            <div className="flex flex-wrap gap-2">
              {exercise.images.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt={exercise.name}
                  loading="lazy"
                  className={`rounded-md border border-zinc-800 bg-zinc-900 object-cover ${
                    exercise.images.length > 1 ? "w-[calc(50%-0.25rem)] min-w-[120px]" : "w-full"
                  }`}
                />
              ))}
            </div>
          )}

          {exercise.desc && (
            <p className="text-sm leading-relaxed text-zinc-400">{exercise.desc}</p>
          )}

          {hasCues && (
            <div>
              <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-300 mb-1.5">
                <ListChecks size={13} className="text-[#fa003f]" /> Come eseguirlo
              </p>
              <ul className="space-y-1">
                {exercise.cues.map((c, i) => (
                  <li key={i} className="flex gap-2 text-sm text-zinc-400">
                    <span className="mt-0.5 text-[#fa003f]">•</span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hasErrors && (
            <div className="border border-amber-500/30 bg-amber-500/[0.06] p-3">
              <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-400 mb-1.5">
                <AlertTriangle size={13} /> Errori da evitare
              </p>
              <ul className="space-y-1">
                {exercise.errors.map((e, i) => (
                  <li key={i} className="flex gap-2 text-sm text-amber-200/80">
                    <span className="mt-0.5 text-amber-400">✕</span>
                    <span>{e}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
